import { useMemo } from 'react';
import {
  useAutocompleteSearch,
  useStableOptions,
} from '@/hooks/useAutocompleteSearch';
import { useGetCommercesQuery } from '@/pages/Business/pages/Commerce/services/commerces.service';
import { Option } from '@/components/inputs/AutocompleteInput';

interface UseCommercesAutocompleteProps {
  search?: string;
  minLength?: number;
}

const useCommercesAutocomplete = ({
  search,
  minLength = 3,
}: UseCommercesAutocompleteProps = {}) => {
  const { debouncedSearch, shouldSearch, isDebouncePending } =
    useAutocompleteSearch(search, minLength);

  const {
    data: commercesData,
    isFetching: isCommercesFetching,
    error: commercesError,
  } = useGetCommercesQuery(
    {
      paginated: { page: 1, pageSize: 1000 },
      filters: {
        name: shouldSearch ? debouncedSearch : '',
      },
    },
    { skip: !shouldSearch }
  );

  const isCommercesLoading = shouldSearch && isCommercesFetching;

  const resolvedCommerceOptions: Option[] = useMemo(() => {
    if (!commercesData?.items) return [];

    return commercesData.items.map((commerce) => ({
      label: commerce.name,
      data: commerce.id,
    }));
  }, [commercesData?.items]);

  const commerceOptions = useStableOptions(resolvedCommerceOptions, {
    isPending: isCommercesFetching || isDebouncePending,
    enabled: shouldSearch || isDebouncePending,
  });

  return {
    commerceOptions,
    isCommercesLoading,
    commercesError,
  };
};

export default useCommercesAutocomplete;
